import telwoord from '../../helpers/intelligence/telwoord'

/**
 * Wat er nog ontbreekt tot de poort, als zin. De drempels komen van de server.
 * @param {{orgs: number, interviews: number, minOrgs: number, minInterviews: number}} gate
 */
const watOntbreekt = (gate) => {
    const delen = []
    const orgs = Math.max(0, gate.minOrgs - gate.orgs)
    const ivs = Math.max(0, gate.minInterviews - gate.interviews)
    if (orgs > 0) delen.push(telwoord(orgs, 'organisatie', 'organisaties'))
    if (ivs > 0) delen.push(telwoord(ivs, 'interview', 'interviews'))
    return delen.length ? `Nog ${delen.join(' en ')} tot een kwalitatief inzicht.` : ''
}

/**
 * Eén interview op het thema. Het citaat is wat de organisatie bij dít effect
 * heeft goedgekeurd; het 'waarom' is de duiding van de begeleider of
 * deelnemer. Niet-gereviewde interviews tonen géén citaat.
 *
 * @param {object} props
 * @param {object} props.row - Regel uit getThemeInterviews
 * @param {boolean} [props.unreviewed]
 */
const InterviewRow = ({ row, unreviewed = false }) => (
    <div className="intelligence-agenda-row">
        <div className="intelligence-agenda-row__body">
            <p className="intelligence-agenda-row__title">
                {row.orgName} <span className="intelligence-badge intelligence-badge--dimensie">{row.role}</span>
                {unreviewed && <span className="intelligence-badge intelligence-badge--review">nog niet gereviewd</span>}
            </p>
            {!unreviewed && row.evidence && <blockquote className="intelligence-quote">{row.evidence}</blockquote>}
            {!unreviewed && row.explanation && <span className="intelligence-agenda-row__stand">{row.explanation}</span>}
            {!unreviewed && row.anekdotes && row.anekdotes.length > 0 && (
                <details className="intelligence-anekdotes">
                    <summary>{telwoord(row.anekdotes.length, 'anekdote', 'anekdotes')}</summary>
                    {row.anekdotes.map((a, i) => <blockquote key={i} className="intelligence-quote">{a}</blockquote>)}
                </details>
            )}
        </div>
    </div>
)

/**
 * Interviews van een thema: wat begeleiders en deelnemers over dit effect bij
 * deze doelgroep zeggen. Intern; de redacteur leest dit vóór die tekst schrijft.
 *
 * Alleen eigen interviews (effect én doelgroep). Effectbrede interviews staan
 * hier niet (besluit user 2026-09-14); niet-gereviewde wél, gemarkeerd, zodat
 * te zien is wat er nog niet naar buiten mag.
 *
 * Kwalitatief telt niet mee in de trede van het thema; het heeft een eigen
 * poort, en die komt van de server.
 * AI-CONTEXT: See docs/pitfalls.md#dev-gate-counter-hides-its-own-policy
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {object} props
 * @param {object} props.theme - Het thema
 * @param {{data: (object|null), loading: boolean, loaded: boolean, error: (Error|null)}} props.interviews - Uit useThemeInterviews
 * @param {((voorstel: object) => void)|null} props.onOpenInsight - Opent het kwalitatieve inzicht van dit thema; zonder handler geen knop
 * @param {boolean} [props.embedded] - Als tabblad binnen de inzichtensectie: geen eigen kop
 */
const ThemeInterviewsSection = ({ theme, interviews, onOpenInsight = null, embedded = false }) => {
    const inhoud = () => {
        if (interviews.error) {
            return (
                <p className="intelligence-state intelligence-state--error">
                    De interviews konden niet worden geladen: {interviews.error.message}
                </p>
            )
        }
        if (!interviews.loaded || !interviews.data) return <p className="intelligence-state">Interviews laden…</p>
        const { own, unreviewed, gate } = interviews.data
        if (!own.length && !unreviewed.length) {
            return <p className="intelligence-state">Nog geen interview voor deze doelgroep op dit effect.</p>
        }
        return (
            <>
                {own.length === 0
                    ? <p className="intelligence-state">Nog geen gereviewd interview voor deze doelgroep op dit effect.</p>
                    : <div className="intelligence-agenda-list">{own.map((row) => <InterviewRow key={row.interviewId} row={row} />)}</div>}
                {/* De poort gehaald: dan is er een inzicht met een eigen pagina.
                    De pagina maakt het concept aan als het er nog niet is. */}
                {gate.passed ? (
                    onOpenInsight && (
                        <button
                            type="button"
                            className="intelligence-row-action"
                            onClick={() => onOpenInsight({
                                cellType: 'interviewVoicesForTheme',
                                params: { msiId: theme.msiId, segment: theme.targetgroup },
                            })}
                        >
                            Bekijk inzicht details
                        </button>
                    )
                ) : (
                    own.length > 0 && <p className="intelligence-agenda-row__gap">{watOntbreekt(gate)}</p>
                )}
                {unreviewed.length > 0 && (
                    <>
                        <p className="intelligence-agenda__kop">Nog niet gereviewd</p>
                        <div className="intelligence-agenda-list">{unreviewed.map((row) => <InterviewRow key={row.interviewId} row={row} unreviewed />)}</div>
                    </>
                )}
            </>
        )
    }

    const uitleg = (
        <p className={embedded ? 'intelligence-agenda__hint' : 'intelligence-panel__hint'}>
            Wat begeleiders en deelnemers over dit effect zeggen, uit de storytelling-interviews.
            Illustreert en verklaart; bewijst niet. Poort: minstens twee organisaties en twee
            gereviewde interviews.
        </p>
    )

    if (embedded) return <>{uitleg}{inhoud()}</>

    return (
        <section className="intelligence-panel">
            <h3 className="intelligence-panel__title">Interviews</h3>
            {uitleg}
            {inhoud()}
        </section>
    )
}

export default ThemeInterviewsSection
